import { useState, type FC } from 'react'
import {
    ActionIcon,
    Popover,
    Text,
    Group,
    Button,
    Stack,
    ThemeIcon,
} from '@mantine/core'
import { IconTrash, IconAlertTriangle } from '@tabler/icons-react'
import { genericMemo } from './genericMemo'

export interface DeleteButtonProps {
    // May be async — DeleteButton awaits it and shows an inline error if it
    // rejects. Any resolved value is ignored.
    onDelete: () => void | Promise<unknown>
    isLoading?: boolean
    title?: string
    confirmTitle?: string
    confirmMessage?: string
    variant?: 'subtle' | 'filled' | 'outline' | 'light' | 'default'
    color?: string
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    disabled?: boolean
}

export const DeleteButton: FC<DeleteButtonProps> = genericMemo(
    ({
        onDelete,
        isLoading = false,
        title = 'Delete',
        confirmTitle = 'Confirm Deletion',
        confirmMessage = 'Are you sure you want to delete this item? This action cannot be undone.',
        variant = 'subtle',
        color = 'red.8',
        size = 'sm',
        disabled = false,
    }) => {
        const [isPopoverOpen, setIsPopoverOpen] = useState(false)
        const [submitting, setSubmitting] = useState(false)
        const [error, setError] = useState<string | null>(null)
        const busy = isLoading || submitting

        // Await onDelete so a failed delete shows inline (popover stays open)
        // rather than silently closing — pending stays on the button throughout.
        const handleConfirm = async () => {
            setError(null)
            setSubmitting(true)
            try {
                await onDelete()
                setIsPopoverOpen(false)
            } catch {
                setError('Could not delete. Please try again.')
            } finally {
                setSubmitting(false)
            }
        }

        const handleCancel = () => {
            setError(null)
            setIsPopoverOpen(false)
        }

        return (
            <Popover
                width={300}
                position='bottom'
                withArrow
                shadow='md'
                opened={isPopoverOpen}
                onChange={(opened) => {
                    setIsPopoverOpen(opened)
                    if (!opened) setError(null)
                }}
                portalProps={{}}
            >
                <Popover.Target>
                    <ActionIcon
                        variant={variant}
                        color={color}
                        size={size}
                        loading={busy}
                        disabled={disabled}
                        onClick={(event) => {
                            event.stopPropagation()
                            setIsPopoverOpen(true)
                        }}
                        title={title}
                        aria-label={title}
                    >
                        <IconTrash size={16} />
                    </ActionIcon>
                </Popover.Target>

                <Popover.Dropdown onClick={(event) => event.stopPropagation()}>
                    <Stack gap='md'>
                        <Group gap='xs'>
                            <ThemeIcon color='warning' variant='outline' bd={0}>
                                <IconAlertTriangle size={20} />
                            </ThemeIcon>
                            <Text fw={600} size='sm'>
                                {confirmTitle}
                            </Text>
                        </Group>

                        <Text size='sm' c='dimmed'>
                            {confirmMessage}
                        </Text>

                        {error && (
                            <Text size='sm' c='error'>
                                {error}
                            </Text>
                        )}

                        <Group gap='xs' justify='flex-end'>
                            <Button
                                variant='subtle'
                                color='gray'
                                size='xs'
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-autofocus
                                variant='filled'
                                color='error'
                                size='xs'
                                onClick={() => void handleConfirm()}
                                loading={busy}
                            >
                                Delete
                            </Button>
                        </Group>
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        )
    }
)
